import React, { useEffect, useState, useMemo } from "react";
import {
  Col, Container, Row, Card, CardBody, Spinner, Badge,
  Button, Input, Nav, NavItem, NavLink, Table,
  InputGroup, InputGroupText, Collapse
} from "reactstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import classnames from "classnames";

import Breadcrumbs from "../../components/Common/Breadcrumb";
import { syncInvoiceToParasut } from "../../helpers/parasut_helper";
import { Link } from "react-router-dom";

const STATUS_TABS = [
  { key: "all",      label: "Tümü" },
  { key: "Ödendi",   label: "Ödendi" },
  { key: "Bekliyor", label: "Bekliyor" },
  { key: "İptal",    label: "İptal" },
];

const PARASUT_TABS = [
  { key: "all",      label: "Tümü" },
  { key: "synced",   label: "Paraşüt'te Kayıtlı" },
  { key: "unsynced", label: "Senkronize Edilmedi" },
];

const getCustomerName = (inv) =>
  inv?.Users_Invoince?.Users_Company?.Users_Company_Name ||
  inv?.Users?.Users_NameSurname ||
  null;

const InvoicesList = () => {
  document.title = "Faturalar | LojGo Yönetim";

  const [invoices, setInvoices]     = useState([]);
  const [isLoading, setIsLoading]   = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sendingId, setSendingId]   = useState(null);
  const [statusTab, setStatusTab]   = useState("all");
  const [parasutTab, setParasutTab] = useState("all");
  const [dateFrom, setDateFrom]     = useState("");
  const [dateTo, setDateTo]         = useState("");

  const [isOpenMap, setIsOpenMap] = useState({}); 
  const [isSavingItem, setIsSavingItem] = useState(null); 
  const [newItemData, setNewItemData] = useState({}); 

  const toggleInvoiceRows = (id) => {
    setIsOpenMap(prev => ({ ...prev, [id]: !prev[id] }));
    if (!newItemData[id]) {
      setNewItemData(prev => ({ ...prev, [id]: { ProductName: "", Quantity: 1, UnitPrice: "", VatRate: 20 } }));
    }
  };

  const handleAddItem = async (invoiceId) => {
    const item = newItemData[invoiceId];
    if (!item.ProductName || !item.UnitPrice) {
      toast.warning("Lütfen ürün adı ve fiyat giriniz.");
      return;
    }

    setIsSavingItem(invoiceId);
    try {
      const newQ = parseFloat(item.Quantity) || 1;
      const newP = parseFloat(item.UnitPrice) || 0;
      const newV = item.VatRate !== null && item.VatRate !== undefined ? parseFloat(item.VatRate) : 20;

      const lineResponse = await fetch("/api/hiflow/Invoince_Lines", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          Invoince_ID: invoiceId,
          ProductName: item.ProductName,
          Quantity: newQ,
          UnitPrice: newP,
          VatRate: newV
        }),
      });

      if (!lineResponse.ok) throw new Error("Kalem eklenemedi.");

      const currentInvoice = invoices.find(inv => inv.ID === invoiceId);
      const existingLines = currentInvoice?.Invoince_Lines || [];

      let newAmountHT = 0;
      let newAmountVAT = 0;

      if (existingLines.length > 0) {
        existingLines.forEach(line => {
          const q = parseFloat(line.Quantity) || 1;
          const p = parseFloat(line.UnitPrice) || 0;
          const v = line.VatRate !== null && line.VatRate !== undefined ? parseFloat(line.VatRate) : 20;

          newAmountHT += (q * p);
          newAmountVAT += (q * p) * (v / 100);
        });
      }

      newAmountHT += (newQ * newP);
      newAmountVAT += (newQ * newP) * (newV / 100);
      const newTotalAmount = newAmountHT + newAmountVAT;

      const invoiceResponse = await fetch(`/api/hiflow/Invoince(${invoiceId})`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          Amount_HT: newAmountHT,
          Amount_VAT: newAmountVAT,
          TotalAmount: newTotalAmount
        })
      });

      if (!invoiceResponse.ok) throw new Error("Fatura genel toplamı güncellenemedi.");

      toast.success("Kalem eklendi ve fatura toplamı eşitlendi.");
      setNewItemData(prev => ({ ...prev, [invoiceId]: { ProductName: "", Quantity: 1, UnitPrice: "", VatRate: 20 } }));
      fetchInvoices(); 

    } catch (error) {
      console.error("Kalem ekleme hatası:", error);
      toast.error(`Hata: ${error.message}`);
    } finally {
      setIsSavingItem(null);
    }
  };

  const fetchInvoices = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        "/api/hiflow/Invoince?$orderby=Created_DateTime desc&$expand=Users,Users_Invoince($expand=Users_Company),Invoince_Lines"
      );
      if (!response.ok) throw new Error(`Sunucu Hatası: ${response.status}`);
      const result = await response.json();
      setInvoices(result?.value ?? []);
    } catch (error) {
      console.error("Fatura listesi çekilemedi:", error);
      toast.error("Faturalar yüklenirken hata oluştu.");
      setInvoices([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchInvoices(); }, []);

  const handleQuickSync = async (invoice) => {
    setSendingId(invoice.ID);
    try {
      const response = await fetch(
        `/api/hiflow/Invoince?$filter=ID eq ${invoice.ID}&$expand=Users($expand=Users_BankAccount),Users_Invoince($expand=Users_Company),Invoince_Lines`
      );
      const json = await response.json();
      
      const fullInvoice = json?.value ? json.value[0] : json;
      
      if (!fullInvoice.Invoince_Lines && fullInvoice.Invoince_Lines_Value) {
        fullInvoice.Invoince_Lines = fullInvoice.Invoince_Lines_Value;
      }

      const parasutId = await syncInvoiceToParasut(fullInvoice);
      
      setInvoices(prev =>
        prev.map(inv =>
          inv.ID === invoice.ID ? { ...inv, Parasut_Invoice_ID: parasutId } : inv
        )
      );
      toast.success(`Fatura Paraşüt'e gönderildi! ID: ${parasutId}`);
    } catch (error) {
      console.error("Paraşüt senkronizasyon hatası:", error);
      toast.error(`Paraşüt hatası: ${error.message}`);
    } finally {
      setSendingId(null);
    }
  };

  const filtered = useMemo(() => {
    return invoices.filter(inv => {
      if (statusTab !== "all" && inv.Invoince_Status !== statusTab) return false;
      if (parasutTab === "synced"   && !inv.Parasut_Invoice_ID) return false;
      if (parasutTab === "unsynced" &&  inv.Parasut_Invoice_ID) return false;

      if (dateFrom && new Date(inv.Created_DateTime) < new Date(dateFrom)) return false;
      if (dateTo && new Date(inv.Created_DateTime) > new Date(dateTo + "T23:59:59")) return false;

      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        const matchNo      = inv.Invoince_Number?.toLowerCase().includes(s) ?? false;
        const matchName    = getCustomerName(inv)?.toLowerCase().includes(s) ?? false;
        if (!matchNo && !matchName) return false;
      }

      return true;
    });
  }, [invoices, statusTab, parasutTab, dateFrom, dateTo, searchTerm]);

  const stats = useMemo(() => {
    const total    = invoices.length;
    const paid     = invoices.filter(i => i.Invoince_Status === "Ödendi").length;
    const pending  = invoices.filter(i => i.Invoince_Status === "Bekliyor").length;
    const synced   = invoices.filter(i => i.Parasut_Invoice_ID).length;
    const sumTotal = invoices.reduce((a, i) => a + (parseFloat(i.TotalAmount) || 0), 0);
    return { total, paid, pending, synced, sumTotal };
  }, [invoices]);

  const getStatusBadge = (status) => {
    const map = { "Ödendi": "success", "Bekliyor": "warning", "İptal": "danger" };
    return map[status] ?? "secondary";
  };

  const fmt = (n) => (parseFloat(n) || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 }) + " ₺";
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("tr-TR") : "—";

  const clearFilters = () => {
    setStatusTab("all"); setParasutTab("all"); setDateFrom(""); setDateTo(""); setSearchTerm("");
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Finans" breadcrumbItem="Fatura Listesi" />

          {/* ... (Özet Kartlar ve Filtre Paneli aynı kalacak) ... */}
           {/* ── Özet Kartlar ── */}
           <Row className="mb-4">
            {[
              { label: "Toplam Fatura", value: stats.total,   color: "primary", icon: "bx-receipt" },
              { label: "Ödendi",        value: stats.paid,    color: "success", icon: "bx-check-circle" },
              { label: "Bekliyor",      value: stats.pending, color: "warning", icon: "bx-time-five" },
              { label: "Paraşüt'te",    value: stats.synced,  color: "info",    icon: "bx-cloud-upload" },
            ].map((s, i) => (
              <Col sm={6} xl={3} key={i} className="mb-3">
                <Card className="border-0 shadow-sm h-100">
                  <CardBody className="d-flex align-items-center gap-3">
                    <div className={`avatar-sm rounded bg-${s.color} bg-opacity-10 d-flex align-items-center justify-content-center`}>
                      <i className={`bx ${s.icon} font-size-22 text-${s.color}`}></i>
                    </div>
                    <div>
                      <p className="text-muted mb-1 font-size-13">{s.label}</p>
                      <h4 className="mb-0 fw-bold">{s.value}</h4>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          <Card className="mb-4 border-0 shadow-sm" style={{ background: "linear-gradient(135deg,#556ee6,#6f42c1)" }}>
            <CardBody className="text-white d-flex justify-content-between align-items-center py-3">
              <div>
                <p className="mb-0 opacity-75 font-size-13">Toplam Fatura Tutarı (Tüm Zamanlar)</p>
                <h3 className="mb-0 fw-bold">{fmt(stats.sumTotal)}</h3>
              </div>
              <i className="bx bx-money font-size-40 opacity-25"></i>
            </CardBody>
          </Card>

          <Card className="mb-3 border-0 shadow-sm">
            <CardBody>
              <Row className="g-2 align-items-end">
                <Col md={4}>
                  <label className="form-label font-size-12 text-muted mb-1">Ara (Fatura No / Müşteri / Şirket)</label>
                  <InputGroup>
                    <InputGroupText><i className="bx bx-search"></i></InputGroupText>
                    <Input
                      placeholder="Fatura no, müşteri veya şirket ara..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </Col>
                <Col md={2}>
                  <label className="form-label font-size-12 text-muted mb-1">Başlangıç Tarihi</label>
                  <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                </Col>
                <Col md={2}>
                  <label className="form-label font-size-12 text-muted mb-1">Bitiş Tarihi</label>
                  <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
                </Col>
                <Col md={2}>
                  <label className="form-label font-size-12 text-muted mb-1">Paraşüt Durumu</label>
                  <Input type="select" value={parasutTab} onChange={e => setParasutTab(e.target.value)}>
                    {PARASUT_TABS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </Input>
                </Col>
                <Col md={2} className="d-flex gap-2">
                  <Button color="light" className="w-100" onClick={clearFilters} title="Filtreleri Temizle">
                    <i className="bx bx-reset me-1"></i>Temizle
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Nav tabs className="nav-tabs-custom mb-3">
            {STATUS_TABS.map(t => {
              const count = t.key === "all"
                ? invoices.length
                : invoices.filter(i => i.Invoince_Status === t.key).length;
              return (
                <NavItem key={t.key}>
                  <NavLink
                    className={classnames({ active: statusTab === t.key })}
                    onClick={() => setStatusTab(t.key)}
                    style={{ cursor: "pointer" }}
                  >
                    {t.label}
                    <Badge color={statusTab === t.key ? "primary" : "secondary"} className="ms-2">{count}</Badge>
                  </NavLink>
                </NavItem>
              );
            })}
          </Nav>

          {isLoading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <div className="mt-2 text-muted">Veriler Yükleniyor...</div>
            </div>
          ) : filtered.length === 0 ? (
            <Card><CardBody className="text-center text-muted py-5">
              <i className="bx bx-folder-open font-size-36 d-block mb-2"></i>
              Bu kriterlere uyan fatura bulunamadı.
            </CardBody></Card>
          ) : (
            <Card className="border-0 shadow-sm">
              <CardBody className="p-0">
                <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
                  <span className="text-muted font-size-13">{filtered.length} fatura listeleniyor</span>
                  <Button color="light" size="sm" onClick={fetchInvoices}>
                    <i className="bx bx-refresh me-1"></i>Yenile
                  </Button>
                </div>
                <div className="table-responsive">
                  <Table hover className="mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th className="ps-3">Fatura No</th>
                        <th>Müşteri / Şirket</th>
                        <th>Tarih</th>
                        <th className="text-end">Matrah</th>
                        <th className="text-end">KDV</th>
                        <th className="text-end">Toplam</th>
                        <th className="text-center">Durum</th>
                        <th className="text-center">Paraşüt</th>
                        <th className="text-end pe-3">İşlem</th>
                      </tr>
                    </thead>
                   <tbody>
                    {filtered.map((invoice) => {
                      const customerName = getCustomerName(invoice);
                      const companyName = invoice.Users_Invoince?.Users_Company?.Users_Company_Name;
                      const userName = invoice.Users?.Users_NameSurname;
                      const isOpen = !!isOpenMap[invoice.ID];

                      return (
                        <React.Fragment key={invoice.ID}>
                          <tr>
                            <td className="ps-3 fw-semibold">
                              <Button 
                                color="link" 
                                size="sm" 
                                className="p-0 me-2 text-decoration-none" 
                                onClick={() => toggleInvoiceRows(invoice.ID)}
                              >
                                <i className={`bx ${isOpen ? 'bx-chevron-up' : 'bx-chevron-down'} font-size-18`}></i>
                              </Button>
                              <Link to={`/invoice-detail/${invoice.ID}`} className="text-primary">
                                {invoice.Invoince_Number || <span className="text-muted fst-italic">Taslak</span>}
                              </Link>
                            </td>
                            <td>
                              {customerName ? (
                                <div>
                                  <span className="text-dark fw-semibold">{customerName}</span>
                                  {companyName && userName && (
                                    <div className="text-muted font-size-12">{userName}</div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted fst-italic">İsimsiz</span>
                              )}
                            </td>
                            <td className="text-muted">{fmtDate(invoice.Created_DateTime)}</td>
                            <td className="text-end">{fmt(invoice.Amount_HT)}</td>
                            <td className="text-end text-muted">{fmt(invoice.Amount_VAT)}</td>
                            <td className="text-end fw-bold">{fmt(invoice.TotalAmount)}</td>
                            <td className="text-center">
                              <Badge color={getStatusBadge(invoice.Invoince_Status)} className="px-2">
                                {invoice.Invoince_Status || "Bekliyor"}
                              </Badge>
                            </td>
                            <td className="text-center">
                              {invoice.Parasut_Invoice_ID ? (
                                <span className="text-success" title={`ID: ${invoice.Parasut_Invoice_ID}`}>
                                  <i className="mdi mdi-cloud-check font-size-16"></i>
                                </span>
                              ) : (
                                <span className="text-muted">
                                  <i className="mdi mdi-cloud-off-outline font-size-16"></i>
                                </span>
                              )}
                            </td>
                            <td className="text-end pe-3">
                              <div className="d-flex justify-content-end gap-1">
                                {!invoice.Parasut_Invoice_ID && (
                                  <Button
                                    color="soft-primary"
                                    size="sm"
                                    disabled={sendingId === invoice.ID}
                                    onClick={() => handleQuickSync(invoice)}
                                  >
                                    {sendingId === invoice.ID ? <Spinner size="sm" /> : <><i className="mdi mdi-cloud-upload me-1"></i>Gönder</>}
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>

                          {/* ── AÇILIR DETAY ALANI ── */}
                          <tr>
                            <td colSpan="9" className="p-0 border-0">
                              <Collapse isOpen={isOpen}>
                                <div className="bg-light p-3 border-bottom">
                                  <Row className="g-3">
                                    
                                    {/* 1. Sütun: Müşteri Fatura Bilgileri */}
                                    <Col md={3}>
                                      <div className="bg-white p-2 border rounded h-100">
                                        <h6 className="font-size-11 text-uppercase fw-bold mb-2 text-primary border-bottom pb-1">
                                          <i className="bx bx-building me-1"></i>Fatura Bilgileri
                                        </h6>
                                        <div className="font-size-12">
                                          <p className="mb-1"><strong>VKN/TCKN:</strong> {invoice.Users_Invoince?.KdvNumber || invoice.Users?.Users_Tc || "—"}</p>
                                          <p className="mb-1"><strong>V. Dairesi:</strong> {invoice.Users_Invoince?.VergiDairesi || "—"}</p>
                                          <p className="mb-1 text-truncate" title={invoice.Users_Invoince?.Adres}>
                                            <strong>Adres:</strong> {invoice.Users_Invoince?.Adres || "—"}
                                          </p>
                                          {invoice.Users_Invoince?.Iban && (
                                            <p className="mb-0 text-primary"><strong>IBAN:</strong> {invoice.Users_Invoince.Iban}</p>
                                          )}
                                        </div>
                                      </div>
                                    </Col>

                                    {/* 2. Sütun: Mevcut Kalemler Listesi */}
                                    <Col md={5}>
                                      <div className="bg-white p-2 border rounded h-100">
                                        <h6 className="font-size-11 text-uppercase fw-bold mb-2 text-muted border-bottom pb-1">Mevcut Kalemler</h6>
                                        <Table size="sm" className="mb-0 table-borderless">
                                          <thead>
                                            <tr className="font-size-11 border-bottom">
                                              <th>Hizmet</th>
                                              <th className="text-center">Adet</th>
                                              <th className="text-end">Birim Fiyat</th>
                                              <th className="text-center">KDV</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {invoice.Invoince_Lines?.map(line => (
                                              <tr key={line.ID} className="font-size-12 border-bottom">
                                                <td className="py-1">{line.ProductName}</td>
                                                <td className="text-center py-1">{line.Quantity}</td>
                                                <td className="text-end py-1">{fmt(line.UnitPrice)}</td>
                                                <td className="text-center py-1">%{line.VatRate}</td>
                                              </tr>
                                            ))}
                                            {(!invoice.Invoince_Lines || invoice.Invoince_Lines.length === 0) && (
                                              <tr><td colSpan="4" className="text-center text-muted p-2 font-size-12">Kalem bulunamadı.</td></tr>
                                            )}
                                          </tbody>
                                        </Table>
                                      </div>
                                    </Col>

                                    {/* 3. Sütun: Hızlı Kalem Ekleme Formu */}
                                    <Col md={4}>
                                      <div className="bg-white p-2 border rounded h-100">
                                        <h6 className="font-size-11 text-uppercase fw-bold mb-2 text-muted border-bottom pb-1">Hızlı Kalem Ekle</h6>
                                        <Row className="g-2">
                                          <Col md={12}>
                                            <Input size="sm" placeholder="Hizmet Adı" 
                                              value={newItemData[invoice.ID]?.ProductName || ""}
                                              onChange={(e) => setNewItemData(prev => ({...prev, [invoice.ID]: {...prev[invoice.ID], ProductName: e.target.value}}))}
                                            />
                                          </Col>
                                          <Col md={3}>
                                            <Input size="sm" type="number" placeholder="Mik." 
                                              value={newItemData[invoice.ID]?.Quantity || ""}
                                              onChange={(e) => setNewItemData(prev => ({...prev, [invoice.ID]: {...prev[invoice.ID], Quantity: e.target.value}}))}
                                            />
                                          </Col>
                                          <Col md={3}>
                                            <Input size="sm" type="number" placeholder="Fiyat" 
                                              value={newItemData[invoice.ID]?.UnitPrice || ""}
                                              onChange={(e) => setNewItemData(prev => ({...prev, [invoice.ID]: {...prev[invoice.ID], UnitPrice: e.target.value}}))}
                                            />
                                          </Col>
                                          <Col md={3}>
                                            <Input size="sm" type="select" 
                                              value={newItemData[invoice.ID]?.VatRate ?? 20}
                                              onChange={(e) => setNewItemData(prev => ({...prev, [invoice.ID]: {...prev[invoice.ID], VatRate: e.target.value}}))}
                                            >
                                              <option value={0}>%0</option>
                                              <option value={1}>%1</option>
                                              <option value={10}>%10</option>
                                              <option value={20}>%20</option>
                                            </Input>
                                          </Col>
                                          <Col md={3}>
                                            <Button color="primary" size="sm" className="w-100" 
                                              disabled={isSavingItem === invoice.ID || !!invoice.Parasut_Invoice_ID}
                                              onClick={() => handleAddItem(invoice.ID)}
                                            >
                                              {isSavingItem === invoice.ID ? <Spinner size="sm" /> : "Ekle"}
                                            </Button>
                                          </Col>
                                        </Row>
                                        {invoice.Parasut_Invoice_ID && (
                                          <div className="mt-2 text-danger font-size-11 text-center">
                                            <i className="bx bx-lock-alt"></i> Senkronize fatura düzenlenemez.
                                          </div>
                                        )}
                                      </div>
                                    </Col>
                                  </Row>
                                </div>
                              </Collapse>
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          )}

        </Container>
      </div>
      <ToastContainer autoClose={2500} position="bottom-right" />
    </React.Fragment>
  );
};

export default InvoicesList;